import React, { forwardRef } from "react";
import logo from "../../HslLogo.png";
import PointsChart from "./PointsChart";
import { C, Crest, FONT, InlineImages, OddsBar, PredictedScore, formatMatchdayDate, pct, useInlineImage } from "./common";
import { forecastNextMatchday } from "../../utils/predictions";

export const POSTER_WIDTH = 1080;
export const POSTER_HEIGHT = 1440;
const PAD = 40;
const GAP = 18;
const INNER = POSTER_WIDTH - PAD * 2;

const box = {
  background: C.panel,
  border: `1px solid ${C.border}`,
  borderRadius: 20,
  padding: "16px 22px",
  minWidth: 0,
  overflow: "hidden",
  boxSizing: "border-box",
};
const title = { color: C.muted, fontSize: 17, fontWeight: 800, letterSpacing: 1.8, marginBottom: 8 };
function PosterLogo() {
  return <img src={useInlineImage(logo)} alt="HSL" style={{ height: 64, width: "auto" }} />;
}

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

/** Up to 4 cards per row: 8 teams → 4×2, 6 → 3×2, 4 → 4×1. */
const oddsColumns = (n) => (n <= 4 ? n : Math.min(5, Math.ceil(n / 2)));
const ODDS_CARD_H = 140;
const ODDS_GAP = 12;
const GAME_CARD_H = 158;
const GAME_GAP = 14;
const BOX_CHROME = 32 + 25 + 8;

const oddsHeight = (n) => {
  const rows = Math.ceil(n / oddsColumns(n));
  return BOX_CHROME + rows * ODDS_CARD_H + (rows - 1) * ODDS_GAP;
};
const nextHeight = (games) => {
  const rows = Math.ceil(Math.min(games, 6) / 2);
  return BOX_CHROME + rows * GAME_CARD_H + (rows - 1) * GAME_GAP;
};

const Stat = ({ label, value, color }) => (
  <div style={{ flex: 1, minWidth: 0, background: C.panelHi, borderRadius: 10, padding: "6px 10px" }}>
    <div style={{ color: C.muted, fontSize: 12, fontWeight: 800, letterSpacing: 1, whiteSpace: "nowrap" }}>{label}</div>
    <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1.1, color }}>{value}</div>
  </div>
);

function SeasonOdds({ predictions }) {
  const rows = predictions.table;
  const cols = oddsColumns(rows.length);
  return (
    <div style={{ ...box, flex: "0 0 auto", height: oddsHeight(rows.length) }}>
      <div style={title}>PROJECTED FINISH · CHANCE TO WIN THE LEAGUE & FINISH TOP 3</div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: ODDS_GAP }}>
        {rows.map((r, i) => (
          <div
            key={r.teamId}
            style={{
              height: ODDS_CARD_H,
              boxSizing: "border-box",
              border: `1px solid ${i === 0 ? C.accent : C.border}`,
              borderRadius: 14,
              padding: "10px 12px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              minWidth: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <Crest url={r.logoUrl} name={r.name} size={50} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ ...twoLines(17), fontWeight: 800 }}>{r.name}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.muted, whiteSpace: "nowrap" }}>
                  <span style={{ color: i === 0 ? C.accent : C.text, fontWeight: 900 }}>{ordinal(i + 1)}</span> · {Math.round(r.projectedPoints)} pts
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Stat label="WIN LEAGUE" value={pct(r.title)} color={r.title >= 0.25 ? C.win : r.title < 0.02 ? C.muted : C.text} />
              <Stat label="TOP 3" value={pct(r.top3)} color={r.top3 >= 0.5 ? C.win : r.top3 < 0.1 ? C.loss : C.text} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Names wrap to two lines instead of using an ellipsis: the PNG capture measures text slightly
// differently from the browser, which truncated even short names.
const twoLines = (fontSize) => ({
  fontSize,
  lineHeight: 1.15,
  maxHeight: fontSize * 1.15 * 2,
  overflow: "hidden",
  overflowWrap: "anywhere",
  paddingRight: 2,
});
const teamName = { ...twoLines(17), fontWeight: 800, width: "100%", textAlign: "center" };

function NextUp({ predictions, teamsById }) {
  const { nextMatchday, nextGames } = predictions;
  const games = nextGames.slice(0, 6);
  const standingName = Object.fromEntries((predictions.table || []).map((r) => [r.teamId, r.name]));
  return (
    <div style={{ ...box, flex: "0 0 auto", height: nextHeight(games.length) }}>
      <div style={title}>
        NEXT UP · MATCHDAY {nextMatchday.matchday}{" "}
        <span style={{ fontWeight: 600 }}>· {formatMatchdayDate(nextMatchday.date, { weekday: "short", month: "short", day: "numeric" })}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: GAME_GAP }}>
        {games.map((g) => {
          const home = { ...teamsById[g.homeTeamId], name: standingName[g.homeTeamId] || teamsById[g.homeTeamId]?.name || "TBD" };
          const away = { ...teamsById[g.awayTeamId], name: standingName[g.awayTeamId] || teamsById[g.awayTeamId]?.name || "TBD" };
          return (
            <div
              key={g.fixtureId}
              style={{ height: GAME_CARD_H, boxSizing: "border-box", background: C.panelHi, borderRadius: 14, padding: "12px 16px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}
            >
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto minmax(0,1fr)", alignItems: "start", gap: 10 }}>
                <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 0 }}>
                  <Crest url={home.logo_url} name={home.name} size={58} />
                  <div style={teamName}>{home.name}</div>
                </span>
                <span style={{ alignSelf: "center", marginTop: -14 }}>
                  <PredictedScore score={g.likelyScore} size={46} />
                </span>
                <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 0 }}>
                  <Crest url={away.logo_url} name={away.name} size={58} />
                  <div style={teamName}>{away.name}</div>
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 16, color: C.muted, fontWeight: 800 }}>
                <span style={{ width: 48 }}>{pct(g.home)}</span>
                <OddsBar home={g.home} draw={g.draw} away={g.away} height={10} />
                <span style={{ width: 48, textAlign: "right" }}>{pct(g.away)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const HEADER_H = 66;
const FOOTER_H = 18;

/** Fixed 1080×1440 (3:4) Instagram poster: points race to the next matchday, season odds per team, next matchday. */
const Poster = forwardRef(function Poster({ report, meta, images = {}, predictions, teamsById = {} }, ref) {
  const hasNext = predictions?.nextMatchday && predictions.nextGames.length > 0;
  const hasOdds = !!predictions?.table;
  const forecast = forecastNextMatchday(report.standings, predictions);
  const sections = 2 + Number(hasOdds) + Number(hasNext);
  const chartBox =
    POSTER_HEIGHT - PAD * 2 - HEADER_H - FOOTER_H - GAP * sections -
    (hasOdds ? oddsHeight(predictions.table.length) : 0) -
    (hasNext ? nextHeight(predictions.nextGames.length) : 0);
  const chartHeight = Math.max(260, chartBox - 14 - 6 - 25 - 8);
  return (
    <InlineImages.Provider value={images}>
      <div
        ref={ref}
        style={{
          width: POSTER_WIDTH,
          height: POSTER_HEIGHT,
          boxSizing: "border-box",
          padding: PAD,
          background: "#f4f5f7",
          color: C.text,
          fontFamily: FONT,
          display: "flex",
          flexDirection: "column",
          gap: GAP,
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 18, height: 66, flex: "0 0 auto" }}>
          <PosterLogo />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1, letterSpacing: -0.5, textTransform: "uppercase", whiteSpace: "nowrap" }}>
              {meta.divisionLabel} <span style={{ color: C.muted }}>· SEASON OVERVIEW</span>
            </div>
            <div style={{ color: C.muted, fontSize: 18, fontWeight: 600, marginTop: 6 }}>
              HSL Insights · {meta.city} · {meta.seasonLabel} · after matchday {report.matchday} of {report.matchdays.length} ·{" "}
              {formatMatchdayDate(report.matchdayDate, { month: "short", day: "numeric" })}
            </div>
          </div>
        </div>

        <div style={{ ...box, padding: "14px 20px 6px", flex: "0 0 auto" }}>
          <div style={title}>
            POINTS RACE <span style={{ color: C.accent }}>· AFTER MD{report.matchday}</span>
            {forecast && (
              <span style={{ color: C.forecast, float: "right", letterSpacing: 1 }}>
                <svg width="36" height="10" style={{ marginRight: 8, verticalAlign: "middle" }}>
                  <line x1="0" y1="5" x2="36" y2="5" stroke={C.forecast} strokeWidth="3" strokeDasharray="7 5" />
                </svg>
                FORECAST MD{forecast.matchday}
              </span>
            )}
          </div>
          <PointsChart report={report} forecast={forecast} width={INNER - 42} height={chartHeight} poster lastMatchday={forecast?.matchday ?? report.matchday} />
        </div>

        {hasOdds && <SeasonOdds predictions={predictions} />}
        {hasNext && <NextUp predictions={predictions} teamsById={teamsById} />}

        <div style={{ display: "flex", justifyContent: "space-between", color: C.muted, fontSize: 15, fontWeight: 600, height: 18, flex: "0 0 auto", marginTop: "auto" }}>
          <span>Predictions simulate the remaining fixtures from goals scored and conceded so far.</span>
          <span style={{ color: C.accent, fontWeight: 800 }}>stats.hadafsports.com/insights</span>
        </div>
      </div>
    </InlineImages.Provider>
  );
});

export default Poster;
